import type { UsageResponse, GroupBy } from "../types";

// Same-origin: billing-admin.leiaa.com/api/v1/... routes to billing-api in prod.
// Vite dev server proxies /api to localhost:8080.
const API_BASE = "/api/v1";

export interface UsageQueryParams {
  customerId: string;
  start: Date;
  end: Date;
  groupBy?: GroupBy;
  filters?: Partial<Record<GroupBy, string>>;
}

export class BillingApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`${status}: ${detail}`);
  }
}

export async function fetchUsage(params: UsageQueryParams): Promise<UsageResponse> {
  const query = new URLSearchParams({
    start: params.start.toISOString(),
    end: params.end.toISOString(),
  });
  if (params.groupBy) {
    query.set("group_by", params.groupBy);
  }
  if (params.filters) {
    for (const [k, v] of Object.entries(params.filters)) {
      if (v) query.set(k, v);
    }
  }

  const url = `${API_BASE}/customers/${encodeURIComponent(params.customerId)}/usage?${query}`;
  const resp = await fetch(url, {
    credentials: "include", // include IAP cookie
    headers: { Accept: "application/json" },
  });

  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      detail = body.detail ?? detail;
    } catch {
      /* response wasn't JSON */
    }
    throw new BillingApiError(resp.status, detail);
  }

  return (await resp.json()) as UsageResponse;
}

// Current logged-in user (from IAP-injected header forwarded by billing-api).
export async function fetchMe(): Promise<{ email: string } | null> {
  try {
    const resp = await fetch(`${API_BASE}/me`, { credentials: "include" });
    if (!resp.ok) return null;
    return (await resp.json()) as { email: string };
  } catch {
    return null;
  }
}

export type AuthState = "ok" | "forbidden" | "unauthenticated";
export const LOGIN_URL = "/api/v1/login";
// IAP's built-in sign-out: appending this mode to any IAP-gated URL clears the
// IAP session cookie. Routes through the gated /api path so IAP intercepts it.
export const LOGOUT_URL = "/api/v1/login?gcp-iap-mode=CLEAR_LOGIN_COOKIE";
export const LOGGED_OUT_FLAG = "iapLoggedOut";

// Clear the IAP session cookie without following IAP's auto re-login redirect.
// redirect:"manual" stops after the first (cookie-clearing) response, so the
// browser applies the expiring Set-Cookie but we don't bounce back into login.
export async function clearIapSession(): Promise<void> {
  try {
    await fetch(LOGOUT_URL, { credentials: "include", redirect: "manual" });
  } catch {
    /* opaque-redirect / network — the cookie-clearing Set-Cookie still applies */
  }
}

// Probe /me to decide what to render. "unauthenticated" => no IAP session (IAP
// returns a cross-origin redirect, seen here as an opaqueredirect / network
// error) -> caller bounces to LOGIN_URL. "forbidden" => signed in but not a
// billing admin; do NOT redirect or it would loop.
export async function checkAuth(): Promise<{ state: AuthState; email?: string }> {
  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}/me`, {
      credentials: "include",
      redirect: "manual",
      headers: { Accept: "application/json" },
    });
  } catch {
    return { state: "unauthenticated" };
  }
  // IAP returns an opaque redirect (browser nav) or 401 (XHR) when there's no
  // valid session — both mean "send the user to log in". 403 means signed in
  // but not a billing admin.
  if (resp.type === "opaqueredirect" || resp.status === 0 || resp.status === 401) {
    return { state: "unauthenticated" };
  }
  if (resp.ok) {
    const body = (await resp.json()) as { email: string };
    return { state: "ok", email: body.email };
  }
  return { state: "forbidden" };
}
